// A Yes/No custom field's popover body on the Opportunities list: the same list surface as the dropdown
// editor, with Yes and No, the current one tinted, and a save on selection. There is no None row: a value
// that is not set reads as No, so No is the marked row then, and picking the marked row saves nothing.
import React, {useRef} from 'react';
import {Text} from '@forecast-it/design-system';
import {BOOLEAN_FALSE, BOOLEAN_TRUE} from '../../../../shared/util/customFieldValueValidation';
import {List, Option} from '../editorOptionList';
import {saveCustomFieldValue} from './saveCustomFieldValue';
import type {CustomFieldEditorProps} from './CustomFieldEditor';

export const BooleanFieldEditor = ({node, def, current, onCommit, close, labels}: CustomFieldEditorProps): JSX.Element => {
	// Anything but 'true' reads No, the same as the cell shows it.
	const effective = current === BOOLEAN_TRUE ? BOOLEAN_TRUE : BOOLEAN_FALSE;
	// The popover stays mounted through its exit animation, so a second click could otherwise save again.
	const saving = useRef(false);
	const select = (value: string) => {
		if (saving.current) return;
		saving.current = true;
		close();
		// Re-picking the marked value would commit a no-op mutation and refetch the whole list.
		if (value === effective) return;
		saveCustomFieldValue({node, def, value, onCommit});
	};
	const choices = [
		{value: BOOLEAN_TRUE, label: labels.yes},
		{value: BOOLEAN_FALSE, label: labels.no},
	];
	return (
		<List data-cy="cf-boolean-editor">
			{choices.map(choice => (
				<Option
					key={choice.value}
					type="button"
					selected={choice.value === effective}
					aria-current={choice.value === effective ? 'true' : undefined}
					data-cy={`cf-option-${choice.value}`}
					onClick={() => select(choice.value)}
				>
					<Text>{choice.label}</Text>
				</Option>
			))}
		</List>
	);
};

export default BooleanFieldEditor;
